import axios from 'axios';
import { APP_CONFIG } from '../app/config';
import { OPERATOR_LOGO_ALLOWED_TYPES } from '../branding/brandingPresentation';
import { getToken, useSessionStore } from '../session/store';

// ─── Axios Instance ────────────────────────────────────────────────────────────
// Token is read from session store, not directly from localStorage.
const api = axios.create({
  baseURL: APP_CONFIG.apiBaseUrl,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res.data,
  (err) => {
    // In standalone-token mode, a 401 means the token expired — clear session.
    // Skip this in dev-open and algosys-embedded modes to avoid logout loops.
    if (err.response?.status === 401) {
      const { mode, clearSession } = useSessionStore.getState();
      if (mode === 'standalone-token') clearSession();
    }

    const status  = err.response?.status;
    const rawDetail = err.response?.data?.detail;

    // Log 422 bodies clearly so we can see what param the backend requires.
    // Requests carrying secrets opt out: FastAPI echoes the rejected input.
    if (status === 422 && !err.config?.suppressErrorDetailLog) {
      console.warn('[API 422]', err.config?.url, JSON.stringify(rawDetail, null, 2));
    }

    // FastAPI detail can be an array of validation errors — flatten to readable string
    let message;
    if (Array.isArray(rawDetail)) {
      message = rawDetail.map((e) => `${e.loc?.join('.')}: ${e.msg}`).join('; ');
    } else {
      message = rawDetail || err.response?.data?.message || err.message || 'Unknown error';
    }

    const error = new Error(message);
    error.status = status;
    error.data   = err.response?.data;
    return Promise.reject(error);
  }
);

// ─── Public (pre-login) Axios Instance ─────────────────────────────────────────
// Deliberately interceptor-free. The shared `api` instance above attaches the
// stored bearer token and clears the session on any 401 — neither is acceptable
// for a decorative, unauthenticated request, which must never be able to end an
// authenticated session or depend on a JWT.
const publicApi = axios.create({
  baseURL: APP_CONFIG.apiBaseUrl,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

// Domain verification/approval/routing and logo references are Super Admin or
// future-phase concerns, and the Operator's registered email/phone are owned by
// the Super Admin account record, so only these fields may ever reach the
// self-service PATCH.
export const OPERATOR_BRANDING_EDITABLE_FIELDS = Object.freeze([
  'business_name',
  'primary_color',
  'proposed_domain',
]);

// Mirrors the backend OperatorSmtpCredentialsRequest provider Literal. SMTP host,
// port and TLS mode are fixed backend presets and are never sent.
export const OPERATOR_SMTP_PROVIDERS = Object.freeze(['gmail', 'microsoft']);

// Mirrors the backend OperatorDomainLifecycleActionRequest action Literal.
export const OPERATOR_DOMAIN_LIFECYCLE_ACTIONS = Object.freeze([
  'verify',
  'approve',
  'enable_routing',
  'disable_routing',
  'reset_to_proposed',
]);

function isPositiveIntegerId(value) {
  return Number.isInteger(value) && value > 0;
}

// The rejection carries only the HTTP status, never the backend body, so no
// echoed request input can reach React Query state, logs or the UI.
function safeSmtpCredentialError(err) {
  const error = new Error('SMTP credential request failed');
  error.status = err?.status;
  return error;
}

function pickOperatorBrandingChanges(changes) {
  const body = {};
  for (const field of OPERATOR_BRANDING_EDITABLE_FIELDS) {
    if (changes && Object.prototype.hasOwnProperty.call(changes, field)) body[field] = changes[field];
  }
  return body;
}

// ─── Route Constants ───────────────────────────────────────────────────────────
const R = {
  // Public website — no auth
  WEBSITE_CONTACT: '/website/contact',

  // Identity / Auth
  AUTH_REGISTER:       '/auth/register',
  AUTH_PORTAL_BRANDING: '/auth/portal-branding',
  AUTH_PORTAL_LOGO:     '/auth/portal-logo',
  AUTH_LOGIN:          '/auth/login',
  AUTH_VERIFY_EMAIL:   '/auth/verify-email',
  AUTH_RESEND_VERIFICATION: '/auth/resend-verification',
  AUTH_FORGOT_PASSWORD:'/auth/forgot-password',
  AUTH_RESET_PASSWORD: '/auth/reset-password',
  AUTH_CHANGE_PASSWORD:'/auth/change-password',
  USERS_ME:            '/users/me',
  // Broker support notice contacts. The backend resolves the Operator from the
  // persisted ownership of the caller or the optional target user_id; no
  // Operator selector is ever sent.
  USERS_SUPPORT_CONTACT: '/users/support-contact',
  // Operator self-service branding. The Operator is derived from the bearer
  // token; this route accepts no operator selector.
  OPERATOR_BRANDING:   '/operator/branding',
  // Own-logo upload/replace (PUT raw image bytes), preview (GET PNG) and
  // removal (DELETE). Same ownership rule: no operator selector.
  OPERATOR_BRANDING_LOGO: '/operator/branding/logo',
  // Own SMTP profile (GET status, PATCH disable-only) and credential
  // replacement (PUT). Same ownership rule: no operator selector.
  OPERATOR_BRANDING_SMTP: '/operator/branding/smtp',
  OPERATOR_BRANDING_SMTP_CREDENTIALS: '/operator/branding/smtp/credentials',

  // Chrome extension secure connect flow (Phase 2F)
  EXTENSION_AUTHORIZE: '/extension/auth/authorize',

  // Admin Identity
  ADMIN_USERS:         '/admin/users',
  ADMIN_USER:          (id)   => `/admin/users/${id}`,
  ADMIN_USER_STATUS:   (id)   => `/admin/users/${id}/status`,
  ADMIN_ROLES:         '/admin/roles',
  ADMIN_USER_ROLES:    (id)   => `/admin/users/${id}/roles`,
  ADMIN_USER_ROLE:     (id, role) => `/admin/users/${id}/roles/${role}`,
  // Super Admin-only Operator branding read and domain lifecycle actions.
  ADMIN_OPERATOR_BRANDING:         (id) => `/admin/operators/${id}/branding`,
  ADMIN_OPERATOR_DOMAIN_LIFECYCLE: (id) => `/admin/operators/${id}/domain-lifecycle`,
  ADMIN_USER_OP_LIMITS:(id)   => `/admin/users/${id}/operator-limits`,

  // Broker
  BROKER_CURRENT:      '/broker/current',
  BROKER_MAP:          '/broker/map',
  BROKER_UNMAP:        '/broker/unmap',
  BROKER_CREDENTIALS:  '/broker/credentials',
  BROKER_MAPPING_AVAILABILITY: '/broker/mapping-availability',

  // System
  HEALTH:              '/health',
  REDIS_HEALTH:        '/redis/health',
  SUBSCRIPTIONS:       '/subscriptions',
  SUBSCRIBE:           '/subscribe',
  UNSUBSCRIBE:         '/unsubscribe',
  WARMUP:              '/warmup',
  REPAIR_SYMBOL:       '/repair-symbol',
  RECOVER_SYMBOL:      '/recover-symbol',
  STARTUP_SUBS:        '/subscriptions/startup',
  STARTUP_RELOAD:      '/subscriptions/startup/reload',
  TICKER:              '/ticker',
  CANDLES:             '/candles',
  CANDLES_LATEST:      '/candles/latest',
  SYMBOL_SEARCH:       '/symbols/search',

  // Strategy
  STRATEGIES:          '/strategy/strategies',
  STRATEGY:            (id) => `/strategy/strategies/${id}`,
  // Mapped master strategies (caller-scoped view of operator-owned MASTER_EXTERNAL strategies)
  MAPPED_STRATEGIES:   '/strategy/strategies/mapped',
  MAPPED_STRATEGY:     (id) => `/strategy/strategies/mapped/${id}`,
  // Operator-side master-strategy management
  MASTER_STRATEGY_ENABLE:              (id)         => `/strategy/master-strategies/${id}/enable`,
  MASTER_STRATEGY_ACTIVATE:            (id)         => `/strategy/master-strategies/${id}/activate`,
  MASTER_STRATEGY_DEACTIVATE:          (id)         => `/strategy/master-strategies/${id}/deactivate`,
  MASTER_STRATEGY_STATUS:              (id)         => `/strategy/master-strategies/${id}/status`,
  MASTER_STRATEGY_MAPPINGS:            (id)         => `/strategy/master-strategies/${id}/mappings`,
  MASTER_STRATEGY_MAPPING:             (sid, mid)   => `/strategy/master-strategies/${sid}/mappings/${mid}`,
  MASTER_STRATEGY_FORCE_EXIT_CANDIDATES: (id)       => `/strategy/master-strategies/${id}/force-exit/candidates`,
  MASTER_STRATEGY_FORCE_EXIT_USERS:    (id, symbol) => `/strategy/master-strategies/${id}/force-exit/candidates/${encodeURIComponent(symbol)}/users`,
  MASTER_STRATEGY_FORCE_EXIT:          (id)         => `/strategy/master-strategies/${id}/force-exit`,
  STRATEGY_RESERVE_TOKEN: '/strategy/strategies/reserve-token',
  STRATEGY_DEPLOY:     (id) => `/strategy/strategies/${id}/deploy`,
  STRATEGY_SCHEMA_V1:   '/strategy/schema/v1',
  STRATEGY_SCHEMA_ENUMS:'/strategy/schema/v1/enums',
  STRATEGY_CONDITION_CATALOG:'/strategy/schema/v1/condition-catalog',
  STRATEGY_VALIDATE_V1: '/strategy/schema/v1/validate',
  STRATEGY_EVALUATE_V1: '/strategy/schema/v1/evaluate',
  // Schema-v2 — same endpoint, discriminated by schema_version: "2.0" in the payload
  STRATEGIES_V2:        '/strategy/strategies',
  DEPLOYMENT:          (id) => `/strategy/deployments/${id}`,
  DEPLOYMENT_PAUSE:    (id) => `/strategy/deployments/${id}/pause`,
  DEPLOYMENT_RESUME:   (id) => `/strategy/deployments/${id}/resume`,
  DEPLOYMENT_STOP:     (id) => `/strategy/deployments/${id}/stop`,
  DEPLOYMENT_EXIT_AND_START: (id) => `/strategy/deployments/${id}/exit-and-start`,
  DEPLOYMENT_STATE:    (id) => `/strategy/deployments/${id}/state`,
  DEPLOYMENT_SYMBOL_STATUS: (id) => `/strategy/deployments/${id}/symbol-status`,
  DEPLOYMENT_RUNTIME_SNAPSHOT: (id) => `/strategy/deployments/${id}/runtime-snapshot`,
  DEPLOYMENT_SIGNALS:  (id) => `/strategy/deployments/${id}/signals`,
  DEPLOYMENT_WEBHOOK_EVENTS: (id) => `/strategy/deployments/${id}/webhook-events`,
  DEPLOYMENT_DISPATCH: (id) => `/strategy/deployments/${id}/dispatch-logs`,
  DEPLOYMENT_LOGS:     (id) => `/strategy/deployments/${id}/logs`,
  STRATEGY_POSITIONS:   '/strategy/positions',
  STRATEGY_MANUAL_EXIT: '/strategy/positions/manual-exit',
  MANUAL_TRADING_DEPLOYMENTS: '/strategy/manual-trading/deployments',
  MANUAL_TRADING_WATCHLIST:     (depId) => `/strategy/manual-trading/deployments/${depId}/watchlist`,
  MANUAL_TRADING_WATCHLIST_ROW: (rowId) => `/strategy/manual-trading/watchlist/${rowId}`,
  MANUAL_TRADING_WATCHLIST_SIGNAL: (rowId) => `/strategy/manual-trading/watchlist/${rowId}/signal`,
  MANUAL_TRADING_WATCHLIST_PREPARE: (rowId) => `/strategy/manual-trading/watchlist/${rowId}/prepare`,
  MANUAL_TRADING_WATCHLIST_CONFIRM: (rowId) => `/strategy/manual-trading/watchlist/${rowId}/confirm`,
  MANUAL_TRADING_WATCHLIST_EXIT: (rowId) => `/strategy/manual-trading/watchlist/${rowId}/exit`,
  MANUAL_TRADING_WATCHLIST_EXIT_PREPARE: (rowId) => `/strategy/manual-trading/watchlist/${rowId}/exit/prepare`,
  MANUAL_TRADING_WATCHLIST_EXIT_CONFIRM: (rowId) => `/strategy/manual-trading/watchlist/${rowId}/exit/confirm`,
  MANUAL_TRADING_ORDER_CANCEL_PREPARE: (orderLogId) => `/strategy/manual-trading/orderbook/${orderLogId}/cancel/prepare`,
  MANUAL_TRADING_ORDER_CANCEL_CONFIRM: (orderLogId) => `/strategy/manual-trading/orderbook/${orderLogId}/cancel/confirm`,
  MANUAL_TRADING_RISK_TRIGGERS: (depId) => `/strategy/manual-trading/deployments/${depId}/risk-triggers`,
  MANUAL_TRADING_WATCHLIST_ACTIVE_RISK: (rowId) => `/strategy/manual-trading/watchlist/${rowId}/active-risk`,
  MANUAL_TRADING_TRADE_BOOK: (depId) => `/strategy/manual-trading/deployments/${depId}/trade-book`,
  MANUAL_TRADING_EXIT_ALL: (depId) => `/strategy/manual-trading/deployments/${depId}/exit-all`,
  // Reconcile a locally open Manual LIVE position against the broker (external exits import).
  MANUAL_TRADING_RECONCILE_BROKER: (depId) => `/strategy/manual-trading/deployments/${depId}/reconcile-broker`,
  MANUAL_TRADING_FANOUT_EVENTS: '/strategy/manual-trading/fanout/events',
  MANUAL_TRADING_FANOUT_EVENT: (eventId) => `/strategy/manual-trading/fanout/events/${eventId}`,
  SIGNALS_ALL:         '/strategy/signals',
  WEBHOOK_EVENTS_ALL:  '/strategy/webhook-events',
  DISPATCH_ALL:        '/strategy/dispatch-logs',
  LOGS_ALL:            '/strategy/logs',
  ALERT_AUDIT:         '/strategy/alert-audit',
  TRADING_AUDIT:       '/strategy/trading-audit',

  // Admin
  EXPIRY_CACHE:        '/api/admin/expiry-cache',
  ADMIN_RUNTIME_MONITORING: '/api/admin/runtime/monitoring',

  // O For Option
  OPTIONS_STRATEGIES:  '/api/options/strategies',
  OPTIONS_STRATEGY:    (id) => `/api/options/strategies/${id}`,
  OPTIONS_DEPLOYMENTS: (id) => `/api/options/strategies/${id}/deployments`,
  OPTIONS_TEMPLATES:   '/api/options/templates',

  // Billing — user
  BILLING_SUBSCRIPTION:          '/billing/subscription',
  BILLING_SUB_HISTORY:           '/billing/subscription/history',
  BILLING_PLANS_PUBLIC:          '/billing/plans',
  BILLING_LICENSE_PACKS:         '/billing/license-packs',
  BILLING_ORDERS:                '/billing/orders',
  BILLING_VERIFY_PAYMENT:        '/billing/verify-payment',
  // Owner-scoped pending-payment recovery (white-label Phase 4 Stage 4 APIs).
  // The backend derives ownership from the bearer token — no user/operator id
  // is ever sent from the browser.
  BILLING_PENDING_ORDERS:        '/billing/orders/pending',
  BILLING_VERIFY_PENDING_ORDER:  (orderId) => `/billing/orders/${orderId}/verify-pending`,
  // Operator-owned Razorpay gateway configuration. Operator identity comes from
  // the session token; these routes accept no operator selector.
  BILLING_OPERATOR_GATEWAY:             '/billing/operator-gateway',
  BILLING_OPERATOR_GATEWAY_CREDENTIALS: '/billing/operator-gateway/credentials',

  // Proxy IP binding — user
  BROKER_PROXY_BINDING:          '/broker/proxy-binding',

  // Proxy IP binding — admin
  ADMIN_PROXY_INVENTORY:         '/admin/proxy/inventory',
  ADMIN_PROXY_INVENTORY_ITEM:    (id) => `/admin/proxy/inventory/${id}`,
  ADMIN_PROXY_CAPACITY:          '/admin/proxy/capacity',
  ADMIN_PROXY_BINDINGS:          '/admin/proxy/bindings',
  ADMIN_PROXY_BINDING_RELEASE:   (id) => `/admin/proxy/bindings/${id}/release`,
  ADMIN_PROXY_BINDING_REPLACEMENT_CANDIDATES: (id) => `/admin/proxy/bindings/${id}/replacement-candidates`,
  ADMIN_PROXY_BINDING_REASSIGN:  (id) => `/admin/proxy/bindings/${id}/reassign`,
  ADMIN_PROXY_COOLING:           '/admin/proxy/cooling',
  ADMIN_PROXY_COOLING_EXTEND:    (id) => `/admin/proxy/inventory/${id}/cooling/extend`,
  ADMIN_PROXY_COOLING_CLEAR:     (id) => `/admin/proxy/inventory/${id}/cooling/clear`,
  ADMIN_PROXY_PENDING:           '/admin/proxy/pending',
  ADMIN_PROXY_PENDING_RETRY:     (id) => `/admin/proxy/pending/${id}/retry`,
  ADMIN_PROXY_PENDING_RETRY_ALL: '/admin/proxy/pending/retry-all',

  // Dedicated IPv6 provisioning (Phase B/C1 reservation — Super Admin only)
  ADMIN_PROXY_IPV6_SERVERS:      '/admin/proxy/ipv6-provisioning-servers',
  ADMIN_PROXY_IPV6_RUNS:         '/admin/proxy/ipv6-provisioning-runs',
  ADMIN_PROXY_IPV6_RUN:          (id) => `/admin/proxy/ipv6-provisioning-runs/${id}`,

  // Payments — admin (super_admin only)
  ADMIN_PAYMENTS:                '/admin/payments',
  ADMIN_PAYMENT_DETAIL:          (source, sourceId) => `/admin/payments/${source}/${sourceId}`,
  // Super Admin evidence-based recovery for one billing payment order. Checks
  // authoritative Razorpay evidence only; it can never create or force a payment.
  ADMIN_PAYMENT_BILLING_RECOVER: (orderId) => `/admin/payments/billing/${orderId}/recover`,

  // Google Reviews — admin (super_admin only, Stage 1B/2B)
  ADMIN_GOOGLE_REVIEWS_STATUS:   '/admin/google-reviews/status',
  ADMIN_GOOGLE_REVIEWS_SETTINGS: '/admin/google-reviews/settings',
  ADMIN_GOOGLE_REVIEWS_REVIEWS:  '/admin/google-reviews/reviews',
  ADMIN_GOOGLE_REVIEWS_REVIEW:   (id) => `/admin/google-reviews/reviews/${id}`,
  ADMIN_GOOGLE_REVIEWS_SYNC:     '/admin/google-reviews/sync',
  ADMIN_GOOGLE_REVIEWS_OAUTH_START:      '/admin/google-reviews/oauth/start',
  ADMIN_GOOGLE_REVIEWS_DISCONNECT:       '/admin/google-reviews/disconnect',
  ADMIN_GOOGLE_REVIEWS_LOCATIONS:        '/admin/google-reviews/locations',
  ADMIN_GOOGLE_REVIEWS_LOCATIONS_DISCOVER: '/admin/google-reviews/locations/discover',
  ADMIN_GOOGLE_REVIEWS_LOCATION_SELECT:  (id) => `/admin/google-reviews/locations/${id}/select`,

  // Website Leads — admin (super_admin only)
  ADMIN_WEBSITE_LEADS:           '/admin/website/leads',
  ADMIN_WEBSITE_LEAD:            (id) => `/admin/website/leads/${id}`,
  ADMIN_WEBSITE_LEAD_MARK_SPAM:  (id) => `/admin/website/leads/${id}/mark-spam`,
  ADMIN_WEBSITE_LEAD_CLOSE:      (id) => `/admin/website/leads/${id}/close`,
  ADMIN_WEBSITE_LEAD_SEND_EMAIL: (id) => `/admin/website/leads/${id}/send-email`,

  // Custom Development Strategy Requests — admin (super_admin only)
  ADMIN_CUSTOM_DEV_REQUESTS:      '/admin/website/custom-development/requests',
  ADMIN_CUSTOM_DEV_REQUEST:       (ref) => `/admin/website/custom-development/requests/${ref}`,
  ADMIN_CUSTOM_DEV_STRATEGY_FILE: (ref, fileRef) => `/admin/website/custom-development/requests/${ref}/strategy-files/${fileRef}`,
  ADMIN_CUSTOM_DEV_AGREEMENT_PDF: (agreementRef) => `/admin/website/custom-development/agreements/${agreementRef}/pdf`,
  ADMIN_CUSTOM_DEV_MESSAGES:      (ref) => `/admin/website/custom-development/requests/${ref}/messages`,
  ADMIN_CUSTOM_DEV_MESSAGE_FILE:  (ref, msgRef, fileRef) => `/admin/website/custom-development/requests/${ref}/messages/${msgRef}/files/${fileRef}`,

  // Website Articles — admin (super_admin only)
  ADMIN_WEBSITE_ARTICLES:            '/admin/website/articles',
  ADMIN_WEBSITE_ARTICLE:             (id) => `/admin/website/articles/${id}`,
  ADMIN_WEBSITE_ARTICLE_PUBLISH:     (id) => `/admin/website/articles/${id}/publish`,
  ADMIN_WEBSITE_ARTICLE_UNPUBLISH:   (id) => `/admin/website/articles/${id}/unpublish`,
  ADMIN_WEBSITE_ARTICLE_UPLOAD_IMAGE: '/admin/website/articles/upload-image',
  ADMIN_WEBSITE_ARTICLE_IMPORT_MD:    '/admin/website/articles/import-markdown',

  // Billing — admin
  ADMIN_BILLING_LICENSES:        (opId) => `/admin/billing/licenses/${opId}`,
  ADMIN_BILLING_LICENSES_ALLOC:  (opId) => `/admin/billing/licenses/${opId}/allocate`,
  ADMIN_BILLING_LICENSES_LEDGER: (opId) => `/admin/billing/licenses/${opId}/ledger`,
  ADMIN_BILLING_PLANS:           '/admin/billing/plans',
  ADMIN_BILLING_PLAN:            (id)   => `/admin/billing/plans/${id}`,
  ADMIN_BILLING_USER_SUB:        (uid)  => `/admin/billing/users/${uid}/subscription`,
  ADMIN_BILLING_USER_SUB_HISTORY:(uid)  => `/admin/billing/users/${uid}/subscription/history`,
  ADMIN_BILLING_USER_SUB_ASSIGN: (uid)  => `/admin/billing/users/${uid}/subscription/assign`,
  ADMIN_BILLING_USER_VALIDITY:   (uid)  => `/admin/billing/users/${uid}/validity`,
  ADMIN_BILLING_SUBSCRIPTIONS:   '/admin/billing/subscriptions',
  // License packs — admin
  ADMIN_BILLING_LICENSE_PACKS:        '/admin/billing/license-packs',
  ADMIN_BILLING_LICENSE_PACK:         (id) => `/admin/billing/license-packs/${id}`,
  ADMIN_BILLING_LICENSE_PACK_ASSIGN:  (id) => `/admin/billing/license-packs/${id}/assign`,
  // Operator plan → assign to operator wallet
  ADMIN_BILLING_PLAN_ASSIGN_OP:       (id) => `/admin/billing/plans/${id}/assign-to-operator`,
};

function isUuidLike(value) {
  return typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function normalizeOptionLegForRequest(leg) {
  return {
    id: leg.id,
    action: leg.action,
    optionType: leg.optionType,
    strikeMode: leg.strikeMode,
    offset: Number.isFinite(Number(leg.offset)) ? Number(leg.offset) : 0,
    premiumTarget: leg.premiumTarget === '' || leg.premiumTarget == null
      ? null
      : Number(leg.premiumTarget),
    lots: Math.max(1, Number(leg.lots) || 1),
    ratio: Math.max(1, Number(leg.ratio) || 1),
    expiry: leg.expiry ?? null,
  };
}

function normalizeOptionLegForUi(leg) {
  return {
    id: leg.id,
    action: leg.action,
    optionType: leg.optionType,
    strikeMode: leg.strikeMode,
    offset: Number(leg.offset) || 0,
    premiumTarget: leg.premiumTarget ?? '',
    lots: Math.max(1, Number(leg.lots) || 1),
    ratio: Math.max(1, Number(leg.ratio) || 1),
    expiry: leg.expiry ?? null,
  };
}

function mapOptionStrategyToRequest(strategy) {
  return {
    name: strategy.name,
    family: strategy.family,
    underlying: strategy.underlying,
    expiry: strategy.expiry,
    lotSize: Math.max(1, Number(strategy.lotSize) || 1),
    status: strategy.status || 'saved',
    legs: (strategy.legs || []).map(normalizeOptionLegForRequest),
  };
}

function mapOptionStrategyFromResponse(raw) {
  return {
    id: raw.id,
    userId: raw.userId,
    name: raw.name,
    family: raw.family,
    underlying: raw.underlying,
    expiry: raw.expiry,
    lotSize: raw.lotSize,
    status: raw.status,
    createdAt: raw.createdAt ? String(raw.createdAt).slice(0, 10) : '—',
    updatedAt: raw.updatedAt,
    legs: Array.isArray(raw.legs) ? raw.legs.map(normalizeOptionLegForUi) : [],
  };
}

// ─── Identity / Auth ───────────────────────────────────────────────────────────
// Public, unauthenticated — used by public marketing/funnel pages.
export const websiteApi = {
  submitContact: (payload) => api.post(R.WEBSITE_CONTACT, payload),
};

export const identityApi = {
  register:        (payload)               => api.post(R.AUTH_REGISTER, payload),
  login:           ({ email, password })   => api.post(R.AUTH_LOGIN, { email, password }),
  verifyEmail:     (token)                 => api.post(R.AUTH_VERIFY_EMAIL, { token }),
  resendVerification: (email)              => api.post(R.AUTH_RESEND_VERIFICATION, { email }),
  forgotPassword:  (email)                 => api.post(R.AUTH_FORGOT_PASSWORD, { email }),
  resetPassword:   (token, new_password)   => api.post(R.AUTH_RESET_PASSWORD, { token, new_password }),
  changePassword:  (payload)               => api.post(R.AUTH_CHANGE_PASSWORD, payload),
  me:    ()        => api.get(R.USERS_ME),
  updateMe: (payload) => api.patch(R.USERS_ME, payload),
  // Omit userId for self-service; pass it only for the authorized
  // admin/Operator target-user page. Authenticated instance only.
  supportContact: (userId, config = {}) =>
    api.get(
      R.USERS_SUPPORT_CONTACT,
      userId != null ? { ...config, params: { user_id: userId } } : config,
    ),

  // Public portal branding (white-label Phase 3A contract). Sends no Operator ID,
  // customer ID, hostname or tenant selector — the backend resolves ownership from
  // its own trusted ingress contract. Goes through `publicApi` so no bearer token
  // is attached and a failure cannot clear the session.
  portalBranding: (config) => publicApi.get(R.AUTH_PORTAL_BRANDING, config).then((res) => res.data),

  // Authenticated Operator self-service branding (exact Operator role). Only
  // changed editable fields are sent; `null` clears a field back to the default.
  operatorBranding:       ()        => api.get(R.OPERATOR_BRANDING),
  updateOperatorBranding: (changes) => api.patch(R.OPERATOR_BRANDING, pickOperatorBrandingChanges(changes)),
  // White-label Phase 5C — Operator own logo. All three go through the
  // authenticated instance; ownership comes from the bearer token alone.
  // The preview is fetched as a Blob so the bytes stay inside this client and
  // are never handed a URL from the raw `logo_reference`.
  operatorLogo: (config) => api.get(R.OPERATOR_BRANDING_LOGO, { responseType: 'blob', ...config }),

  // Upload/replace: the File is sent as the raw request body — never multipart
  // or JSON — with its exact image Content-Type, and no filename is sent.
  // The type check is a convenience; the backend re-validates the bytes.
  uploadOperatorLogo: (file) => {
    if (!OPERATOR_LOGO_ALLOWED_TYPES.includes(file?.type)) {
      const error = new Error('Only PNG, JPEG or WebP images are accepted');
      error.status = 415;
      return Promise.reject(error);
    }
    return api.put(R.OPERATOR_BRANDING_LOGO, file, {
      headers: { 'Content-Type': file.type },
      timeout: 60000,
    });
  },

  removeOperatorLogo: () => api.delete(R.OPERATOR_BRANDING_LOGO),

  // White-label Phase 5D — Operator own SMTP profile. The response is status
  // only; the backend never returns a saved app password.
  operatorSmtpProfile: () => api.get(R.OPERATOR_BRANDING_SMTP),

  // Exactly { provider, app_password }; nothing else is ever sent.
  saveOperatorSmtpCredentials: ({ provider, app_password } = {}) => {
    if (!OPERATOR_SMTP_PROVIDERS.includes(provider)) {
      const error = new Error('Unsupported SMTP provider');
      error.status = 422;
      return Promise.reject(error);
    }
    return api
      .put(
        R.OPERATOR_BRANDING_SMTP_CREDENTIALS,
        { provider, app_password },
        { suppressErrorDetailLog: true },
      )
      .catch((err) => Promise.reject(safeSmtpCredentialError(err)));
  },

  // Disable-only: the backend accepts no other value, and there is no enable route.
  disableOperatorSmtp: () => api.patch(R.OPERATOR_BRANDING_SMTP, { enabled: false }),

  // Public current-portal logo (white-label Phase 5C). Goes through the
  // interceptor-free publicApi exactly like `portalBranding`: no bearer token,
  // no Operator selector, and a failure can never clear a session. The backend
  // resolves the approved, routing-enabled Operator portal from its trusted
  // ingress contract and fails closed otherwise.
  portalLogo: (config) =>
    publicApi.get(R.AUTH_PORTAL_LOGO, { responseType: 'blob', ...config }).then((res) => res.data),
};

// Chrome extension secure connect flow — requires a normal authenticated
// website session (Bearer JWT, attached by the request interceptor above).
export const extensionApi = {
  authorize: (payload) => api.post(R.EXTENSION_AUTHORIZE, payload),
};

// ─── Admin Identity ────────────────────────────────────────────────────────────
export const adminIdentityApi = {
  createUser:       (payload)               => api.post(R.ADMIN_USERS, payload),
  listUsers:        ()                    => api.get(R.ADMIN_USERS),
  getUser:          (id)                  => api.get(R.ADMIN_USER(id)),
  updateUserProfile:(id, payload)         => api.patch(R.ADMIN_USER(id), payload),
  setUserStatus:    (id, status)          => api.patch(R.ADMIN_USER_STATUS(id), { status }),
  deleteUser:       (id, mode = 'soft')   => api.delete(R.ADMIN_USER(id), { params: { mode } }),
  listRoles:        ()                    => api.get(R.ADMIN_ROLES),
  assignRole:       (userId, role)        => api.post(R.ADMIN_USER_ROLES(userId), { role }),
  removeRole:       (userId, roleNameOrId)=> api.delete(R.ADMIN_USER_ROLE(userId, roleNameOrId)),
  setOperatorLimits:(userId, payload)     => api.put(R.ADMIN_USER_OP_LIMITS(userId), payload),
  reassignOperator: (userId, operatorId)  => api.patch(`/admin/users/${userId}/operator`, { operator_id: operatorId }),
  // Super Admin custom-domain management. The body is exactly { action }: no
  // hostname, status or operator selector is ever sent; the backend loads the
  // persisted domain for the path Operator and enforces the lifecycle.
  operatorBranding: (operatorId) => {
    if (!isPositiveIntegerId(operatorId)) return Promise.reject(Object.assign(new Error('Invalid Operator'), { status: 400 }));
    return api.get(R.ADMIN_OPERATOR_BRANDING(operatorId));
  },
  updateOperatorDomainLifecycle: (operatorId, action) => {
    if (!isPositiveIntegerId(operatorId)) return Promise.reject(Object.assign(new Error('Invalid Operator'), { status: 400 }));
    if (!OPERATOR_DOMAIN_LIFECYCLE_ACTIONS.includes(action)) {
      return Promise.reject(Object.assign(new Error('Unsupported domain action'), { status: 422 }));
    }
    return api.patch(R.ADMIN_OPERATOR_DOMAIN_LIFECYCLE(operatorId), { action });
  },
};

// ─── Broker ────────────────────────────────────────────────────────────────────
export const brokerApi = {
  current:           (userId)  => api.get(R.BROKER_CURRENT, userId != null ? { params: { user_id: userId } } : undefined),
  getSupported:      ()        => api.get('/broker/supported'),
  getAll:            ()        => api.get('/broker/all'),
  getMappingAvailability: (userId) => api.get(R.BROKER_MAPPING_AVAILABILITY, userId != null ? { params: { user_id: userId } } : undefined),
  // userId targets an admin/operator-managed user; the backend reads it as a query
  // param (not a body field) — passing it in the body is silently ignored.
  map:               (payload, userId) => api.post(R.BROKER_MAP, payload, userId != null ? { params: { user_id: userId } } : undefined),
  unmap:             (userId)  => api.post(R.BROKER_UNMAP, userId != null ? { user_id: userId } : {}),
  unmapCredential:   ({ brokerName, userId }) => api.delete(`/broker/unmap/${brokerName}`, userId != null ? { params: { user_id: userId } } : undefined),
  updateCredentials: (payload) => api.patch(R.BROKER_CREDENTIALS, payload),
  getProxyBinding:   (params)  => api.get(R.BROKER_PROXY_BINDING, params ? { params } : undefined),
  getPaperOrderbook: (params)  => api.get('/broker/paper-orderbook', params ? { params } : undefined),
  getPaperPositions: (params)  => api.get('/broker/paper-positions', params ? { params } : undefined),
};

// ─── Proxy IP Binding — admin ──────────────────────────────────────────────────
export const adminProxyApi = {
  listInventory:   ()            => api.get(R.ADMIN_PROXY_INVENTORY),
  createInventory: (payload)     => api.post(R.ADMIN_PROXY_INVENTORY, payload),
  updateInventory: (id, payload) => api.patch(R.ADMIN_PROXY_INVENTORY_ITEM(id), payload),
  getCapacity:     (params)      => api.get(R.ADMIN_PROXY_CAPACITY, { params }),
  listBindings:    (params)      => api.get(R.ADMIN_PROXY_BINDINGS, { params }),
  releaseBinding:  (id, payload) => api.post(R.ADMIN_PROXY_BINDING_RELEASE(id), payload),
  listReplacementCandidates: (id) => api.get(R.ADMIN_PROXY_BINDING_REPLACEMENT_CANDIDATES(id)),
  reassignBinding: (id, payload) => api.post(R.ADMIN_PROXY_BINDING_REASSIGN(id), payload),
  listCooling:     (params)      => api.get(R.ADMIN_PROXY_COOLING, { params }),
  extendCooling:   (id, payload) => api.post(R.ADMIN_PROXY_COOLING_EXTEND(id), payload),
  clearCooling:    (id, payload) => api.post(R.ADMIN_PROXY_COOLING_CLEAR(id), payload),
  listPending:     (params)      => api.get(R.ADMIN_PROXY_PENDING, { params }),
  retryPending:    (id)          => api.post(R.ADMIN_PROXY_PENDING_RETRY(id)),
  retryAllPending: ()            => api.post(R.ADMIN_PROXY_PENDING_RETRY_ALL),

  // Dedicated IPv6 provisioning (Phase B/C1 reservation — Super Admin only).
  // Phase E adds a sanitized end-to-end `workflow` summary (requested/created/
  // ready/failed/pending counts + is_terminal) to both the collection and the
  // single-run read below; ready/COMPLETED require durable C2 → B.1D success.
  listIpv6ProvisioningServers: () => api.get(R.ADMIN_PROXY_IPV6_SERVERS),
  createIpv6ProvisioningRun: (payload, idempotencyKey) => api.post(
    R.ADMIN_PROXY_IPV6_RUNS,
    payload,
    idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined
  ),
  // Read-only end-to-end workflow discovery for one Proxy Server (Phase E).
  // `active_only=true` returns only nonterminal workflows, newest first.
  listIpv6ProvisioningRuns: (params) => api.get(R.ADMIN_PROXY_IPV6_RUNS, { params }),
  getIpv6ProvisioningRun: (runId) => api.get(R.ADMIN_PROXY_IPV6_RUN(runId)),
};

// ─── Strategies ────────────────────────────────────────────────────────────────
// Manual Trading — deployed Manual Strategies + per-deployment watchlist persistence
export const manualTradingApi = {
  listDeployments:    ()                    => api.get(R.MANUAL_TRADING_DEPLOYMENTS),
  listWatchlist:      (deploymentId)        => api.get(R.MANUAL_TRADING_WATCHLIST(deploymentId)),
  createWatchlistRow: (deploymentId, payload) => api.post(R.MANUAL_TRADING_WATCHLIST(deploymentId), payload),
  updateWatchlistRow: (rowId, payload)      => api.patch(R.MANUAL_TRADING_WATCHLIST_ROW(rowId), payload),
  deleteWatchlistRow: (rowId)               => api.delete(R.MANUAL_TRADING_WATCHLIST_ROW(rowId)),
  // PAPER signal — body MUST be only { action, client_request_id }. Backend derives all execution details.
  submitWatchlistSignal: (rowId, payload)   => api.post(R.MANUAL_TRADING_WATCHLIST_SIGNAL(rowId), payload),
  // LIVE step 1 — prepare a protected LIMIT order. Body { action }. Returns a signed prepared_token +
  // intent_id + frozen pricing (no DB write). Token is held only in component memory, never persisted.
  prepareWatchlistOrder: (rowId, payload)   => api.post(R.MANUAL_TRADING_WATCHLIST_PREPARE(rowId), payload),
  // PAPER/LIVE step 2 — confirm. Privileged MASTER_MANUAL flows may also include fanout_scope.
  confirmWatchlistOrder: (rowId, payload)   => api.post(R.MANUAL_TRADING_WATCHLIST_CONFIRM(rowId), payload),
  // PAPER full square-off — body MUST be only { client_request_id }. Backend derives side/qty/symbol/price.
  exitWatchlistPosition: (rowId, payload)   => api.post(R.MANUAL_TRADING_WATCHLIST_EXIT(rowId), payload),
  // LIVE exit step 1 — preview the protected LIMIT exit. No body; no broker order placed.
  // LIVE order cancel step 1 — preview cancelable pending qty. No body; no broker call.
  prepareOrderCancel: (orderLogId)          => api.post(R.MANUAL_TRADING_ORDER_CANCEL_PREPARE(orderLogId)),
  // LIVE order cancel step 2 — confirm. Body { client_request_id, prepared_token }. Backend reconciles
  // latest broker status, cancels only pending qty, keeps filled qty, may return offer_exit=true.
  confirmOrderCancel: (orderLogId, payload) => api.post(R.MANUAL_TRADING_ORDER_CANCEL_CONFIRM(orderLogId), payload),
  prepareWatchlistExit: (rowId)             => api.post(R.MANUAL_TRADING_WATCHLIST_EXIT_PREPARE(rowId)),
  // LIVE exit step 2 — confirm. Body { client_request_id (==intent_id), prepared_token, limit_price, price_mode }.
  // Fresh broker reconciliation runs on the server before any LIMIT exit is dispatched.
  confirmWatchlistExit: (rowId, payload)    => api.post(R.MANUAL_TRADING_WATCHLIST_EXIT_CONFIRM(rowId), payload),
  // Active risk triggers (read) — newest first, for the current user's deployment.
  getRiskTriggers:    (deploymentId)        => api.get(R.MANUAL_TRADING_RISK_TRIGGERS(deploymentId)),
  // Update the OPEN position's active risk trigger. Body MUST be ONLY { target_trigger_price } OR
  // { stop_loss_trigger_price } (positive = set/enable, 0/null = disable). No watchlist edit.
  updateActiveRisk:   (rowId, payload)      => api.patch(R.MANUAL_TRADING_WATCHLIST_ACTIVE_RISK(rowId), payload),
  // Trade Book (read) — backend-reconstructed trade cycles. Params: date, symbol, status, page, page_size.
  getTradeBook:       (deploymentId, params) => api.get(R.MANUAL_TRADING_TRADE_BOOK(deploymentId), params ? { params } : undefined),
  // Exit All open PAPER positions for a deployment. Body MUST be only { client_request_id }.
  exitAllManualPositions: (deploymentId, body) => api.post(R.MANUAL_TRADING_EXIT_ALL(deploymentId), body),
  // Row-level broker Sync — PREPARE/CONFIRM reconciliation of one locally open Manual LIVE
  // position against the broker. `body` is the full ManualBrokerSyncRequest payload; never
  // reconstructed or altered here.
  reconcileBroker: (deploymentId, body) =>
    api.post(R.MANUAL_TRADING_RECONCILE_BROKER(deploymentId), body),
  // Operator/admin read-only Manual fan-out observability. Responses are already safe summaries.
  listFanoutEvents: (params) => api.get(R.MANUAL_TRADING_FANOUT_EVENTS, params ? { params } : undefined),
  getFanoutEvent: (eventId) => api.get(R.MANUAL_TRADING_FANOUT_EVENT(eventId)),
};

export const strategiesApi = {
  schemaV1: () => api.get(R.STRATEGY_SCHEMA_V1),
  schemaEnums: () => api.get(R.STRATEGY_SCHEMA_ENUMS),
  conditionCatalog: () => api.get(R.STRATEGY_CONDITION_CATALOG),
  validateSchemaV1: (payload) => api.post(R.STRATEGY_VALIDATE_V1, payload),
  evaluateConditionTree: (payload) => api.post(R.STRATEGY_EVALUATE_V1, payload),
  delete: (id) => api.delete(R.STRATEGY(id)),
  create: (payload) => api.post(R.STRATEGIES, payload),

  createV2: (payload) => api.post(R.STRATEGIES_V2, payload),
  reserveToken: (payload = {}) => api.post(R.STRATEGY_RESERVE_TOKEN, payload),

  updateV2: (id, payload) => api.put(R.STRATEGY(id), payload),

  list: () => api.get(R.STRATEGIES),

  get:          (id)      => api.get(R.STRATEGY(id)),
  deploy:       (id, payload) => api.post(R.STRATEGY_DEPLOY(id), payload || {}),
  getPositions: ()        => api.get(R.STRATEGY_POSITIONS),
  manualExitQuote: (payload) => api.post(`${R.STRATEGY_MANUAL_EXIT}/quote`, payload),
  manualExit:   (payload) => api.post(R.STRATEGY_MANUAL_EXIT, payload),
};

// ─── Mapped Master Strategies (caller-scoped) ─────────────────────────────────
export const mappedStrategiesApi = {
  list:   ()               => api.get(R.MAPPED_STRATEGIES),
  get:    (id)             => api.get(R.MAPPED_STRATEGY(id)),
  update: (id, payload)    => api.patch(R.MAPPED_STRATEGY(id), payload),
};

// ─── Operator-side master-strategy management ─────────────────────────────────
export const masterStrategyApi = {
  enable:                (id)              => api.post(R.MASTER_STRATEGY_ENABLE(id)),
  activate:              (id)              => api.post(R.MASTER_STRATEGY_ACTIVATE(id)),
  deactivate:            (id, body = {})   => api.post(R.MASTER_STRATEGY_DEACTIVATE(id), body),
  getStatus:             (id)              => api.get(R.MASTER_STRATEGY_STATUS(id)),
  listMappings:          (id)              => api.get(R.MASTER_STRATEGY_MAPPINGS(id)),
  createMappings:        (id, users)       => api.post(R.MASTER_STRATEGY_MAPPINGS(id), { users }),
  updateMapping:         (sid, mid, payload) => api.patch(R.MASTER_STRATEGY_MAPPING(sid, mid), payload),
  getForceExitCandidates:(id)              => api.get(R.MASTER_STRATEGY_FORCE_EXIT_CANDIDATES(id)),
  getForceExitUsers:     (id, symbol)      => api.get(R.MASTER_STRATEGY_FORCE_EXIT_USERS(id, symbol)),
  forceExit:             (id, payload)     => api.post(R.MASTER_STRATEGY_FORCE_EXIT(id), payload),
};

// ─── Deployments ───────────────────────────────────────────────────────────────
// Get the active deployment for a strategy (returns deployment or null)
export const getActiveDeployment = async (strategyId) => {
  try {
    // Backend: GET /strategy/strategies/{id}/active-deployment
    // Falls back to trying deployment IDs if endpoint doesn't exist yet
    const res = await api.get(`/strategy/strategies/${strategyId}/active-deployment`);
    return res;
  } catch {
    return null;
  }
};

export const deploymentsApi = {
  getActiveDeployment: async (strategyId) => {
    try { return await api.get(`/strategy/strategies/${strategyId}/active-deployment`); }
    catch { return null; }
  },
  get:          (id)        => api.get(R.DEPLOYMENT(id)),
  pause:        (id)        => api.post(R.DEPLOYMENT_PAUSE(id)),
  resume:       (id)        => api.post(R.DEPLOYMENT_RESUME(id)),
  stop:         (id)        => api.post(R.DEPLOYMENT_STOP(id)),
  // Exit open positions first (sends Algosys exit alert), then stop.
  // Passes ?exit_positions=true so backend can dispatch exit before halting.
  exitAndStop:  (id)        => api.post(R.DEPLOYMENT_STOP(id), {}, { params: { exit_positions: true } }),
  // Mode-switch remediation: exit each open position through its opened_in_mode,
  // then start the deployment in target_mode.
  exitAndStart: (id, targetMode) => api.post(R.DEPLOYMENT_EXIT_AND_START(id), { target_mode: targetMode }),
  delete:       (id)        => api.delete(R.DEPLOYMENT(id)),
  getState:     (id)        => api.get(R.DEPLOYMENT_STATE(id)),
  getSymbolStatus: (id)     => api.get(R.DEPLOYMENT_SYMBOL_STATUS(id)),
  getRuntimeSnapshot: (id)   => api.get(R.DEPLOYMENT_RUNTIME_SNAPSHOT(id)),
  getSignals:    (id, params) => api.get(R.DEPLOYMENT_SIGNALS(id), { params }),
  getWebhookEvents: (id, params) => api.get(R.DEPLOYMENT_WEBHOOK_EVENTS(id), { params }),
  getDispatch:   (id, params) => api.get(R.DEPLOYMENT_DISPATCH(id), { params }),
  getLogs:       (id, params) => api.get(R.DEPLOYMENT_LOGS(id), { params }),
  getAllSignals:  (params)    => api.get(R.SIGNALS_ALL,   { params }),
  getAllWebhookEvents: (params) => api.get(R.WEBHOOK_EVENTS_ALL, { params }),
  getAllDispatch: (params)    => api.get(R.DISPATCH_ALL,  { params }),
  getAllLogs:     (params)    => api.get(R.LOGS_ALL,      { params }),
  getAlertAudit:  (params)    => api.get(R.ALERT_AUDIT,   { params }),
};

// —— O For Option ———————————————————————————————————————————————————————————————
export const optionsApi = {
  create: async (strategy) => {
    const res = await api.post(R.OPTIONS_STRATEGIES, mapOptionStrategyToRequest(strategy));
    return mapOptionStrategyFromResponse(res);
  },

  get: async (id) => {
    const res = await api.get(R.OPTIONS_STRATEGY(id));
    return mapOptionStrategyFromResponse(res);
  },

  update: async (id, strategy) => {
    const res = await api.put(R.OPTIONS_STRATEGY(id), mapOptionStrategyToRequest(strategy));
    return mapOptionStrategyFromResponse(res);
  },

  save: async (strategy, { preferUpdate = false } = {}) => {
    const id = strategy?.id;
    const shouldTryUpdate = Boolean(id) && (preferUpdate || isUuidLike(id));
    if (shouldTryUpdate) {
      try {
        return await optionsApi.update(id, strategy);
      } catch (err) {
        if (err?.status !== 404) throw err;
      }
    }
    return optionsApi.create(strategy);
  },

  list: async () => {
    const res = await api.get(R.OPTIONS_STRATEGIES);
    return Array.isArray(res) ? res.map(mapOptionStrategyFromResponse) : [];
  },

  createDeployment: (id, payload) => api.post(R.OPTIONS_DEPLOYMENTS(id), payload),
  listDeployments: (id) => api.get(R.OPTIONS_DEPLOYMENTS(id)),
  templates: () => api.get(R.OPTIONS_TEMPLATES),
};

// ─── Candles ───────────────────────────────────────────────────────────────────
export const candlesApi = {
  get:    (symbol, timeframe, params) =>
    api.get(R.CANDLES, { params: { symbol, timeframe, ...params } }),
  latest: (symbol) => api.get(R.CANDLES_LATEST, { params: { symbol } }),
};

// ─── System / Admin ────────────────────────────────────────────────────────────
export const systemApi = {
  health:        () => api.get(R.HEALTH),
  redisHealth:   () => api.get(R.REDIS_HEALTH),
  symbolSearch:  ({ q = '', segment = 'all', exchange = 'all' } = {}) =>
    api.get(R.SYMBOL_SEARCH, { params: { q, segment, exchange } }),
  subscriptions: () => api.get(R.SUBSCRIPTIONS),
  subscribe:     (p) => api.post(R.SUBSCRIBE, p),
  unsubscribe:   (p) => api.post(R.UNSUBSCRIBE, p),
  warmup:        (p) => api.post(R.WARMUP, p || {}),
  repairSymbol:  (p) => api.post(R.REPAIR_SYMBOL, p || {}),
  recoverSymbol: (p) => api.post(R.RECOVER_SYMBOL, p || {}),
  startupSubs:   ()  => api.get(R.STARTUP_SUBS),
  reloadStartup: ()  => api.post(R.STARTUP_RELOAD),
  runtimeMonitoring: () => api.get(R.ADMIN_RUNTIME_MONITORING),

  // Merged health object for dashboard/admin
  mergedHealth: async () => {
    const [main, redis] = await Promise.allSettled([
      api.get(R.HEALTH),
      api.get(R.REDIS_HEALTH),
    ]);

    const m = main.status  === 'fulfilled' ? main.value  : null;
    const r = redis.status === 'fulfilled' ? redis.value : null;

    // Log raw responses in dev so we can see the exact field names
    if (import.meta.env.DEV) {
      console.debug('[health] /health raw:', m);
      console.debug('[health] /redis/health raw:', r);
    }

    // Normalise to the fields the UI expects regardless of backend field naming.
    // Common patterns: { status: 'ok' }, { connected: true }, { websocket: true }
    const isOk = (v) => v === true || v === 'ok' || v === 'connected' || v === 'healthy';

    // Real API fields:
    //   /health      → { ws_connected: true, active_subscriptions: N, status: "ok", timestamp }
    //   /redis/health → { redis_available: true, status: "ok" }
    const wsOk =
      m == null ? false
      : isOk(m.ws_connected)   // ← real field
        || isOk(m.websocket) || isOk(m.ws) || isOk(m.connected)
        || isOk(m.fyers_ws)  || m.status === 'ok';

    const redisOk =
      r == null ? false
      : isOk(r.redis_available)  // ← real field
        || isOk(r.redis) || isOk(r.connected)
        || r.status === 'ok';

    return {
      // raw fields spread first so our normalised keys override them
      ...(m || {}),
      ...(r || {}),
      // normalised booleans — these are what all UI components read
      websocket:           wsOk,
      redis:               redisOk,
      candle_feed:         wsOk,
      active_subscriptions: m?.active_subscriptions ?? 0,
      last_checked:        new Date().toISOString(),
    };
  },
};

// ─── Market ───────────────────────────────────────────────────────────────────
export const marketApi = {
  getTicker: () => api.get(R.TICKER),
};

// ─── Admin (wrappers over real endpoints) ─────────────────────────────────────
export const adminApi = {
  systemHealth:       systemApi.mergedHealth,
  allUsers:           adminIdentityApi.listUsers,   // Phase 1: /admin/users (was /strategy/users)
  warmup:             systemApi.warmup,
  repair:             systemApi.repairSymbol,
  recover:            systemApi.recoverSymbol,
  expiryCache:        () => api.get(R.EXPIRY_CACHE),
  refreshExpiryCache: () => api.post(`${R.EXPIRY_CACHE}/refresh`),
  strategiesForUser:  (userId) => api.get(R.STRATEGIES, { params: { user_id: userId } }),
  runtimeMonitoring:  systemApi.runtimeMonitoring,
  tradingAudit:       (params) => api.get(R.TRADING_AUDIT, { params }),
  positionSummary:    (userId)       => api.get(`/strategy/admin/users/${userId}/position-summary`),
  // Mark Flat operator recovery: absent action means INSPECT — the request body
  // is omitted entirely (backend default) and no Idempotency-Key is ever sent.
  // Explicit actions carry the body plus the caller's stable per-operation key.
  markDeploymentFlat: (deploymentId, request) => {
    const body = request?.action ? { action: request.action } : undefined;
    const config = request?.action && request?.idempotencyKey
      ? { headers: { 'Idempotency-Key': request.idempotencyKey } }
      : undefined;
    return api.post(`/strategy/admin/deployments/${deploymentId}/mark-flat`, body, config);
  },
  dismissOrder:       (orderId)      => api.post(`/strategy/admin/orders/${orderId}/dismiss`),
};

// ─── Payments (Admin / super_admin only) ───────────────────────────────────────
export const adminPaymentsApi = {
  listPayments: (params)           => api.get(R.ADMIN_PAYMENTS, { params }),
  getPayment:   (source, sourceId) => api.get(R.ADMIN_PAYMENT_DETAIL(source, sourceId)),
  // Returns { outcome: 'paid' | 'pending' | 'support_required', message }.
  // Sends no body: the backend re-reads provider truth and decides on its own.
  recoverBillingPayment: (orderId) => api.post(R.ADMIN_PAYMENT_BILLING_RECOVER(orderId)),
};

// ─── Website Articles (Admin / super_admin only) ───────────────────────────────
export const adminWebsiteArticlesApi = {
  listArticles:     (params)        => api.get(R.ADMIN_WEBSITE_ARTICLES, { params }),
  createArticle:    (payload)       => api.post(R.ADMIN_WEBSITE_ARTICLES, payload),
  getArticle:       (id)            => api.get(R.ADMIN_WEBSITE_ARTICLE(id)),
  updateArticle:    (id, payload)   => api.patch(R.ADMIN_WEBSITE_ARTICLE(id), payload),
  publishArticle:   (id)            => api.post(R.ADMIN_WEBSITE_ARTICLE_PUBLISH(id)),
  unpublishArticle: (id)            => api.post(R.ADMIN_WEBSITE_ARTICLE_UNPUBLISH(id)),
  deleteArticle:    (id)            => api.delete(R.ADMIN_WEBSITE_ARTICLE(id)),
  uploadArticleImage: (file, imageType) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('image_type', imageType);
    return api.post(R.ADMIN_WEBSITE_ARTICLE_UPLOAD_IMAGE, fd, { headers: { 'Content-Type': undefined } });
  },
  importMarkdownArticle: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('parse_only', 'true');
    return api.post(R.ADMIN_WEBSITE_ARTICLE_IMPORT_MD, fd, { headers: { 'Content-Type': undefined } });
  },
};

// ─── Google Reviews (Admin / super_admin only, Stage 1B/2B) ────────────────────
export const adminGoogleReviewsApi = {
  getStatus:      ()              => api.get(R.ADMIN_GOOGLE_REVIEWS_STATUS),
  getSettings:    ()              => api.get(R.ADMIN_GOOGLE_REVIEWS_SETTINGS),
  updateSettings: (payload)       => api.patch(R.ADMIN_GOOGLE_REVIEWS_SETTINGS, payload),
  listReviews:    (params)        => api.get(R.ADMIN_GOOGLE_REVIEWS_REVIEWS, { params }),
  getReview:      (id)            => api.get(R.ADMIN_GOOGLE_REVIEWS_REVIEW(id)),
  requestSync:    (payload)       => api.post(R.ADMIN_GOOGLE_REVIEWS_SYNC, payload),
  startOAuth:        ()           => api.post(R.ADMIN_GOOGLE_REVIEWS_OAUTH_START),
  disconnect:        ()           => api.post(R.ADMIN_GOOGLE_REVIEWS_DISCONNECT),
  listLocations:     ()           => api.get(R.ADMIN_GOOGLE_REVIEWS_LOCATIONS),
  discoverLocations: ()           => api.post(R.ADMIN_GOOGLE_REVIEWS_LOCATIONS_DISCOVER),
  selectLocation:    (id)         => api.post(R.ADMIN_GOOGLE_REVIEWS_LOCATION_SELECT(id)),
};

// ─── Website Leads (Admin / super_admin only) ──────────────────────────────────
export const adminWebsiteApi = {
  listLeads:  (params)        => api.get(R.ADMIN_WEBSITE_LEADS, { params }),
  getLead:    (id)            => api.get(R.ADMIN_WEBSITE_LEAD(id)),
  updateLead: (id, payload)   => api.patch(R.ADMIN_WEBSITE_LEAD(id), payload),
  markSpam:   (id)            => api.post(R.ADMIN_WEBSITE_LEAD_MARK_SPAM(id)),
  closeLead:  (id)            => api.post(R.ADMIN_WEBSITE_LEAD_CLOSE(id)),
  sendEmail:  (id, payload)   => api.post(R.ADMIN_WEBSITE_LEAD_SEND_EMAIL(id), payload),
};

// ─── Custom Development Strategy Requests (Admin / super_admin only) ──────────
// Distinct backend domain from Website Leads (app.custom_development) — kept as
// its own API object so the two never share request/response shapes.
export const adminCustomDevelopmentApi = {
  listRequests: (params) => api.get(R.ADMIN_CUSTOM_DEV_REQUESTS, { params }),
  getRequest:   (requestReference) => api.get(R.ADMIN_CUSTOM_DEV_REQUEST(requestReference)),
  downloadStrategyFile: (requestReference, fileReference) =>
    api.get(R.ADMIN_CUSTOM_DEV_STRATEGY_FILE(requestReference, fileReference), { responseType: 'blob' }),
  downloadAgreementPdf: (agreementReference) =>
    api.get(R.ADMIN_CUSTOM_DEV_AGREEMENT_PDF(agreementReference), { responseType: 'blob' }),
  listMessages: (requestReference, params) =>
    api.get(R.ADMIN_CUSTOM_DEV_MESSAGES(requestReference), { params }),
  sendMessage: (requestReference, formData, idempotencyKey) =>
    api.post(R.ADMIN_CUSTOM_DEV_MESSAGES(requestReference), formData, {
      headers: { 'Content-Type': undefined, 'Idempotency-Key': idempotencyKey },
    }),
  downloadMessageFile: (requestReference, messageReference, fileReference) =>
    api.get(R.ADMIN_CUSTOM_DEV_MESSAGE_FILE(requestReference, messageReference, fileReference), { responseType: 'blob' }),
};

// ─── Billing (User) ────────────────────────────────────────────────────────────
export const billingApi = {
  subscription:        ()                            => api.get(R.BILLING_SUBSCRIPTION),
  subscriptionHistory: ()                            => api.get(R.BILLING_SUB_HISTORY),
  plans:               ()                            => api.get(R.BILLING_PLANS_PUBLIC),
  licensePacks:        ()                            => api.get(R.BILLING_LICENSE_PACKS),
  createOrder:         (item_type, item_id)          => api.post(R.BILLING_ORDERS, { item_type, item_id }),
  verifyPayment:       (razorpay_payment_id, razorpay_order_id, razorpay_signature) =>
    api.post(R.BILLING_VERIFY_PAYMENT, { razorpay_payment_id, razorpay_order_id, razorpay_signature }),

  // ── Owner-scoped pending-payment recovery ───────────────────────────────────
  pendingOrders:       ({ limit = 20, offset = 0 } = {}) =>
    api.get(R.BILLING_PENDING_ORDERS, { params: { limit, offset } }),
  verifyPendingOrder:  (orderId) => api.post(R.BILLING_VERIFY_PENDING_ORDER(orderId)),

  // ── Operator Razorpay gateway configuration (Operator role only) ────────────
  // The key secret is sent once, in this request body only. It is never stored,
  // cached, logged, or returned by the backend.
  operatorGateway:               ()  => api.get(R.BILLING_OPERATOR_GATEWAY),
  replaceOperatorGatewayCredentials: ({ key_id, key_secret, mode }) =>
    api.put(R.BILLING_OPERATOR_GATEWAY_CREDENTIALS, { key_id, key_secret, mode }),
  setOperatorGatewayEnabled:     (enabled) =>
    api.patch(R.BILLING_OPERATOR_GATEWAY, { enabled }),
};

// ─── Billing (Admin) ───────────────────────────────────────────────────────────
export const adminBillingApi = {
  getOperatorWallet:    (opId)        => api.get(R.ADMIN_BILLING_LICENSES(opId)),
  allocateLicenses:     (opId, body)  => api.post(R.ADMIN_BILLING_LICENSES_ALLOC(opId), body),
  getLicenseLedger:     (opId, p)     => api.get(R.ADMIN_BILLING_LICENSES_LEDGER(opId), { params: p }),
  createPlan:           (body)        => api.post(R.ADMIN_BILLING_PLANS, body),
  listPlans:            (p)           => api.get(R.ADMIN_BILLING_PLANS, { params: p }),
  updatePlan:           (id, body)    => api.put(R.ADMIN_BILLING_PLAN(id), body),
  deletePlan:           (id)          => api.delete(R.ADMIN_BILLING_PLAN(id)),
  getUserSubscription:  (uid)         => api.get(R.ADMIN_BILLING_USER_SUB(uid)),
  getUserSubHistory:    (uid)         => api.get(R.ADMIN_BILLING_USER_SUB_HISTORY(uid)),
  assignPlan:           (uid, body)   => api.post(R.ADMIN_BILLING_USER_SUB_ASSIGN(uid), body),
  updateUserValidity:   (uid, body)   => api.patch(R.ADMIN_BILLING_USER_VALIDITY(uid), body),
  listSubscriptions:    (p)           => api.get(R.ADMIN_BILLING_SUBSCRIPTIONS, { params: p }),
  assignPlanToOperator:(id, body) => api.post(R.ADMIN_BILLING_PLAN_ASSIGN_OP(id), body),
  // License packs
  listLicensePacks:    (p)        => api.get(R.ADMIN_BILLING_LICENSE_PACKS, { params: p }),
  createLicensePack:   (body)     => api.post(R.ADMIN_BILLING_LICENSE_PACKS, body),
  updateLicensePack:   (id, body) => api.put(R.ADMIN_BILLING_LICENSE_PACK(id), body),
  deleteLicensePack:   (id)       => api.delete(R.ADMIN_BILLING_LICENSE_PACK(id)),
  assignLicensePack:   (id, body) => api.post(R.ADMIN_BILLING_LICENSE_PACK_ASSIGN(id), body),
};

export default api;
